import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Printer, Save, School, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SchoolDocumentHeader, { schoolLogoFor } from "@/components/SchoolDocumentHeader";
import PrintSignatures from "@/components/PrintSignatures";
import RecordApproval from "@/components/RecordApproval";
import { staffTitles } from "@/lib/staffTitles";

const MINISTRY_LOGO = "/branding/ministry-education.png";
const SCHOOL_NAMES = {
  abaq: "مدرسة عبق العلم العالمية",
  durat: "مدرسة درة الشرق العالمية",
};
const initialData = (user) => ({
  ministry_logo: MINISTRY_LOGO,
  school_logo: schoolLogoFor(user?.school_key),
  school_name: user?.school_name || (SCHOOL_NAMES[user?.school_key]
    ? `${SCHOOL_NAMES[user.school_key]} - ${user?.gender === "female" ? "بنات" : "بنين"}`
    : ""),
  school_type: "مستقل",
  school_schedule: "صباحي",
  stage: "التمهيدي - الابتدائي - المتوسط - الثانوي",
  academic_year: "1448هـ / 2026-2027م",
  address: "",
  phone: "",
  mobile: "",
  email: "",
  actual_classes: "",
  staffed_classes: "",
  students_prep: "",
  students_primary: "",
  students_middle: "",
  students_secondary: "",
  students_total: "",
  principal_name: "",
  principal_civil_id: "",
  principal_qualification: "",
  principal_specialty: "",
  principal_appointment: "",
  principal_education_start: "",
  principal_counseling_start: "",
  principal_experience: "",
  principal_contact: "",
  counselor_name: user?.name || "",
  counselor_civil_id: "",
  counselor_qualification: "",
  counselor_specialty: "",
  counselor_appointment: "",
  counselor_dedication: "",
  counselor_education_start: "",
  counselor_counseling_start: "",
  counselor_experience: "",
  counselor_contact: "",
  deputy_name: "",
  deputy_civil_id: "",
  deputy_qualification: "",
  deputy_specialty: "",
  deputy_appointment: "",
  deputy_dedication: "",
  deputy_education_start: "",
  deputy_counseling_start: "",
  deputy_experience: "",
  deputy_contact: "",
  approval_principal: "",
  approval_signature: "",
  approval_seal: "",
  approval_date: "",
  educational_supervisor: "",
});

function TextCell({ name, data, setData, type = "text", readOnly = false, className = "" }) {
  return (
    <Input
      type={type}
      value={data[name] || ""}
      readOnly={readOnly}
      onChange={(event) => setData((current) => ({ ...current, [name]: event.target.value }))}
      className={`general-table-input ${className}`}
      aria-label={name}
    />
  );
}

export default function GeneralInfo({ readOnly = false, counselorId = null, counselor = null }) {
  const { user: authUser } = useAuth();
  const user = counselor || authUser;
  const canEdit = authUser?.role === "counselor" && !readOnly && (!counselorId || counselorId === authUser.id);
  const titles = staffTitles(user?.gender);
  const [profileRecord, setProfileRecord] = useState(null);
  const [data, setData] = useState(() => initialData(user));
  const [schoolPrincipalName, setSchoolPrincipalName] = useState("");
  const [principalGender, setPrincipalGender] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/profile", { params: counselorId ? { counselor_id: counselorId } : {} })
      .then(({ data: profile }) => {
        if (active) {
          setProfileRecord(profile);
          const saved = profile?.data || {};
          setData({
            ...initialData(user),
            ...saved,
            ministry_logo: saved.ministry_logo?.startsWith("data:image/") ? saved.ministry_logo : MINISTRY_LOGO,
            school_logo: saved.school_logo?.startsWith("data:image/") ? saved.school_logo : schoolLogoFor(user?.school_key),
          });
        }
      })
      .catch((error) => {
        if (active) toast.error(formatErr(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [counselorId, user]);

  useEffect(() => {
      let active = true;
      api.get("/school-principal", { params: counselorId ? { counselor_id: counselorId } : {} })
        .then(({ data: principal }) => {
          if (active) { setSchoolPrincipalName(principal.name || ""); setPrincipalGender(principal.gender || ""); }
        })
        .catch((error) => {
          if (active) toast.error(formatErr(error));
        });
      return () => { active = false; };
  }, [counselorId]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await api.put("/profile", { data, ...(counselorId ? { counselor_id: counselorId } : {}) });
      setProfileRecord(null);
      const response = await api.get("/profile", { params: counselorId ? { counselor_id: counselorId } : {} });
      setProfileRecord(response.data);
      toast.success("تم حفظ بطاقة البيانات");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setSaving(false);
    }
  };

  const printCard = () => {
    const finishPrinting = () => document.body.classList.remove("printing-general-info");
    document.body.classList.add("printing-general-info");
    window.addEventListener("afterprint", finishPrinting, { once: true });
    window.print();
  };

  const remove = async () => {
    if (!canEdit || !window.confirm("هل تريد حذف جميع البيانات المحفوظة في بطاقة المعلومات العامة؟")) return;
    setSaving(true);
    try {
      await api.delete("/profile", { params: counselorId ? { counselor_id: counselorId } : {} });
      setProfileRecord(null);
      setData(initialData(user));
      toast.success("تم حذف بيانات البطاقة");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setSaving(false);
    }
  };

  const studentLabel = user?.gender === "female" ? "عدد الطالبات" : "عدد الطلاب";

  return (
    <div className="general-info-printable space-y-5">
      <style>{`
        .general-card { border: 1px solid #222; background: #fff; padding: 20px; color: #111; }
        .general-title { background: #006837; color: #fff; text-align: center; padding: 10px; font-size: 19px; font-weight: 700; margin: 10px 0 14px; }
        .general-data-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; }
        .general-data-table th, .general-data-table td { border: 1px solid #222; padding: 5px; text-align: center; }
        .general-data-table th { background: #f1f4f2; }
        .general-section-heading { background: #d1e7dd !important; text-align: right !important; font-size: 13px; }
        .general-row-heading { width: 28%; }
        .general-table-input { height: 30px; border: 0; border-radius: 0; padding: 3px; box-shadow: none; text-align: center; }
        .general-table-input:focus { box-shadow: inset 0 0 0 1px #3E6A56; }
        .general-footer { display: grid; grid-template-columns: 1.2fr 1fr; gap: 12px; font-size: 12px; }
        .general-notes { border: 1px solid #222; padding: 10px; line-height: 1.7; }
        .general-signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 12px; text-align: center; font-size: 11px; }
        .general-signatures > div { border-top: 1px solid #222; padding-top: 5px; }
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body.printing-general-info .min-h-screen { display: block !important; min-height: 0 !important; }
          body.printing-general-info main { width: 100% !important; max-width: none !important; padding: 0 !important; }
          body.printing-general-info .platform-shell,
          body.printing-general-info main > *,
          body.printing-general-info .general-info-printable { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }
          .general-info-printable > * { margin-block: 0 !important; }
          .general-card > * { margin-block: 0 !important; }
          .general-card { width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box; border: 0; padding: 0; }
          .general-card { break-inside: avoid; page-break-inside: avoid; }
          .general-print-header { table-layout: fixed; margin: 0 auto 8px !important; }
          .general-print-header td { padding: 3px !important; overflow-wrap: anywhere; }
          .general-print-header img, .general-print-header .school-document-school-placeholder { height: 65px !important; max-height: 65px !important; max-width: 100% !important; object-fit: contain; }
          .general-print-header .school-document-government { font-size: 10pt !important; line-height: 1.3 !important; }
          .general-title { padding: 4px; font-size: 12pt; margin: 4px 0 6px !important; }
          .general-data-table { table-layout: fixed; margin-bottom: 6px !important; font-size: 8pt; }
          .general-data-table th, .general-data-table td { padding: 2px 3px; line-height: 1.2; overflow-wrap: anywhere; }
          .general-section-heading { font-size: 9pt; }
          .general-data-table tr { break-inside: avoid; }
          .general-row-heading { width: 23%; }
          .general-table-input { height: 16px; min-height: 0; padding: 0; font-size: 8pt; line-height: 1.2; background: transparent !important; }
          .general-editable { display: none !important; }
          .general-footer { grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 6px; font-size: 8pt; break-inside: avoid; }
          .general-notes { padding: 4px; line-height: 1.3; }
          .general-notes ul { margin-top: 4px; }
          .general-signatures { gap: 12px; margin-top: 6px !important; font-size: 8pt; break-inside: avoid; }
          .general-title, .general-section-heading, .general-data-table th { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between no-print">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3E6A56]/10 text-[#3E6A56]"><School className="h-5 w-5" /></div>
          <div>
            <h2 className="text-2xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>معلومات عامة</h2>
            <p className="text-sm text-[#4A554E]">بطاقة بيانات المدرسة و{titles.counselor} للعام الدراسي</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printCard} className="rounded-xl"><Printer className="me-2 h-4 w-4" /> طباعة / PDF</Button>
          {canEdit && <>
            <Button variant="outline" onClick={remove} disabled={saving || loading} className="rounded-xl text-red-700 hover:bg-red-50">
              <Trash2 className="me-2 h-4 w-4" /> حذف البيانات
            </Button>
            <Button onClick={save} disabled={saving || loading} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="general-save-button">
              <Save className="me-2 h-4 w-4" />{saving ? "جاري الحفظ..." : "حفظ البيانات"}
            </Button>
          </>}
        </div>
      </div>

      <div className="general-card space-y-3">
        <RecordApproval section="profile" counselorId={counselorId} counselorGender={user?.gender} contentCount={profileRecord?.updated_at || 0} />
        <SchoolDocumentHeader
          schoolKey={user?.school_key}
          gender={user?.gender}
          schoolName={data.school_name}
          ministryLogo={data.ministry_logo || MINISTRY_LOGO}
          schoolLogo={data.school_logo}
          className="general-print-header"
        />
        {canEdit && <div className="general-editable mb-3 flex flex-wrap items-center justify-end gap-3 text-xs">
          <label className="flex min-w-0 basis-full items-center gap-2"><strong className="shrink-0">المرحلة:</strong><TextCell name="stage" data={data} setData={setData} readOnly={!canEdit} className="min-w-0 flex-1" /></label>
        </div>}

        <div className="general-title">بطاقة بيانات المدرسة للعام الدراسي <TextCell name="academic_year" data={data} setData={setData} readOnly={!canEdit} /></div>

        <table className="general-data-table">
          <tbody>
            <tr><th colSpan="8" className="general-section-heading">1 - بيانات المدرسة</th></tr>
            <tr>
              <td>اسم المدرسة</td><td colSpan="3"><TextCell name="school_name" data={data} setData={setData} readOnly={!canEdit} /></td>
              <td>نوع المبنى</td><td><div className="flex justify-center gap-2">{["مستقل", "مشترك"].map((option) => <label key={option} className="whitespace-nowrap"><input type="radio" name="school_type" disabled={!canEdit} checked={data.school_type === option} onChange={() => setData((current) => ({ ...current, school_type: option }))} /> {option}</label>)}</div></td>
              <td>دوام المدرسة</td><td><div className="flex justify-center gap-2">{["صباحي", "مسائي"].map((option) => <label key={option} className="whitespace-nowrap"><input type="radio" name="school_schedule" disabled={!canEdit} checked={data.school_schedule === option} onChange={() => setData((current) => ({ ...current, school_schedule: option }))} /> {option}</label>)}</div></td>
            </tr>
            <tr>
              <td>العنوان</td><td colSpan="3"><TextCell name="address" data={data} setData={setData} readOnly={!canEdit} /></td>
              <td>رقم الهاتف</td><td><TextCell name="phone" data={data} setData={setData} readOnly={!canEdit} /></td>
              <td>رقم الجوال</td><td><TextCell name="mobile" data={data} setData={setData} readOnly={!canEdit} /></td>
            </tr>
            <tr>
              <td>البريد الإلكتروني</td><td colSpan="3"><TextCell name="email" data={data} setData={setData} type="email" readOnly={!canEdit} /></td>
              <td>عدد الفصول (الفعلي)</td><td><TextCell name="actual_classes" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
              <td>حسب الملاك</td><td><TextCell name="staffed_classes" data={data} setData={setData} readOnly={!canEdit} /></td>
            </tr>
            <tr><td rowSpan="2">{studentLabel}</td><th>تمهيدي</th><th>ابتدائي</th><th>متوسط</th><th>ثانوي</th><th colSpan="3">الإجمالي</th></tr>
            <tr>
              <td><TextCell name="students_prep" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
              <td><TextCell name="students_primary" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
              <td><TextCell name="students_middle" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
              <td><TextCell name="students_secondary" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
              <td colSpan="3"><TextCell name="students_total" data={data} setData={setData} type="number" readOnly={!canEdit} /></td>
            </tr>
          </tbody>
        </table>

        <table className="general-data-table">
          <tbody>
            <tr><th style={{ width: "28%" }}>البيان</th><th>2 - {staffTitles(principalGender || user?.gender).leader}</th><th>3 - {titles.counselor}</th><th>{titles.deputy}</th></tr>
            {[
              ["الاسم رباعياً", "name"], ["السجل المدني", "civil_id"], ["المؤهل وتاريخه", "qualification"],
              ["التخصص", "specialty"], ["تاريخ التعيين", "appointment"], ["التفرغ للإرشاد", "dedication"],
              ["بداية الخدمة في التعليم", "education_start"], ["بداية الخدمة في الإرشاد", "counseling_start"],
              ["سنوات الخبرة", "experience"], ["الجوال / البريد", "contact"],
            ].map(([label, suffix]) => (
              <tr key={suffix}>
                <th className="general-row-heading">{label}</th>
                {["principal", "counselor", "deputy"].map((prefix) => (
                  <td key={prefix}>
                    {suffix === "dedication" && prefix === "principal" ? "—" : suffix === "dedication" ? (
                      <div className="flex justify-center gap-3">{["كلياً", "جزئياً"].map((option) => <label key={option}><input type="radio" disabled={!canEdit} name={`${prefix}_dedication`} checked={data[`${prefix}_dedication`] === option} onChange={() => setData((current) => ({ ...current, [`${prefix}_dedication`]: option }))} /> {option}</label>)}</div>
                    ) : <TextCell name={`${prefix}_${suffix}`} data={data} setData={setData} readOnly={!canEdit} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="general-footer">
          <div className="general-notes">
            <strong>تنبيهات هامة:</strong>
            <ul className="me-4 list-disc">
              <li>يراعى أن تكون البيانات دقيقة ومعتمدة من قائد / قائدة المدرسة وتُختم بختم المدرسة.</li>
              <li>تُكتب بالحاسب الآلي وتُرسل أصل وصورة.</li>
              <li><strong>مفرغة كلياً:</strong> معلم / معلمة تم تفريغه بالكامل للإرشاد.</li>
              <li><strong>مفرغة جزئياً:</strong> لديه / لديها نصاب تدريسي جزئي.</li>
              <li>المدارس التي تحوي أكثر من مرحلة يتم إعداد بيان مستقل لكل مرحلة.</li>
            </ul>
          </div>
          <table className="general-data-table">
            <tbody>
              <tr><th colSpan="2">الاعتماد والتوقيع</th></tr>
              <tr><td>{staffTitles(principalGender || user?.gender).principal}</td><td><TextCell name="approval_principal" data={data} setData={setData} readOnly={!canEdit} /></td></tr>
              <tr><td>التوقيع</td><td><TextCell name="approval_signature" data={data} setData={setData} readOnly={!canEdit} /></td></tr>
              <tr><td>الختم</td><td><TextCell name="approval_seal" data={data} setData={setData} readOnly={!canEdit} /></td></tr>
              <tr><td>التاريخ</td><td><TextCell name="approval_date" data={data} setData={setData} readOnly={!canEdit} /></td></tr>
              <tr><td>المشرف / المشرفة التربوية</td><td><TextCell name="educational_supervisor" data={data} setData={setData} readOnly={!canEdit} /></td></tr>
            </tbody>
          </table>
        </div>
        <PrintSignatures className="general-signatures" counselorName={counselor?.name || data.counselor_name || (!counselorId && user?.role === "counselor" ? user.name : "")} principalName={schoolPrincipalName || data.principal_name || data.approval_principal} counselorGender={user?.gender} principalGender={principalGender} />

        {loading && <p className="no-print text-center text-sm text-[#4A554E]">جاري تحميل البيانات المحفوظة...</p>}
      </div>
      {canEdit && <div className="no-print flex justify-end gap-2">
        <Button variant="outline" onClick={remove} disabled={saving || loading} className="rounded-xl text-red-700 hover:bg-red-50">
          <Trash2 className="me-2 h-4 w-4" /> حذف البيانات
        </Button>
        <Button onClick={save} disabled={saving || loading} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]">
          <Save className="me-2 h-4 w-4" />{saving ? "جاري الحفظ..." : "حفظ البيانات"}
        </Button>
      </div>}
    </div>
  );
}
